import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';

// 用户反馈队列：App「我 → 帮助与反馈」提交的问题/建议。
// GET  /v1/admin/feedback?status= → 列表
// POST /v1/admin/feedback/:id/status body{status, note} → 流转状态

type Feedback = {
  id: string;
  userId?: string;
  category?: string;
  content?: string;
  contact?: string;
  screenshots?: string[];
  appVersion?: string;
  platform?: string;
  region?: string;
  status?: string;
  handledBy?: string;
  adminNote?: string;
  createdAt?: string;
};

type Filter = '' | 'open' | 'in_progress' | 'resolved';

const FILTERS: { key: Filter; label: string }[] = [
  { key: '', label: '全部' },
  { key: 'open', label: '待处理' },
  { key: 'in_progress', label: '处理中' },
  { key: 'resolved', label: '已处理' },
];

const CATEGORY_LABEL: Record<string, string> = {
  bug: '问题',
  suggestion: '建议',
  other: '其他',
};

export default function FeedbackQueuePage() {
  const [list, setList] = useState<Feedback[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('open');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    setList(null);
    try {
      const q = filter ? `?status=${filter}` : '';
      const r = await api<{ items?: Feedback[] }>(`/admin/feedback${q}`);
      setList(r.items ?? []);
    } catch (e) {
      setErr((e as ApiError).message);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id: string, status: string) => {
    const note = prompt('处理备注（可空）：', '') ?? '';
    setBusy(id);
    try {
      await api(`/admin/feedback/${id}/status`, {
        method: 'POST',
        json: { status, note },
      });
      await load();
    } catch (e) {
      alert(`更新失败：${(e as ApiError).message}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div style={S.header}>
        <h1 style={{ margin: 0 }}>用户反馈</h1>
        <button style={S.refresh} onClick={load}>
          刷新
        </button>
      </div>

      <div style={S.filters}>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            style={{
              ...S.filterBtn,
              ...(filter === f.key ? S.filterBtnActive : {}),
            }}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {err && <div style={S.err}>加载失败：{err}</div>}
      {list === null && <div style={S.loading}>加载中…</div>}
      {list && list.length === 0 && (
        <div style={S.empty}>🎉 没有反馈。</div>
      )}

      {list && list.length > 0 && (
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>时间</th>
              <th style={S.th}>类型</th>
              <th style={S.th}>内容</th>
              <th style={S.th}>联系方式</th>
              <th style={S.th}>环境</th>
              <th style={S.th}>状态</th>
              <th style={S.th}>操作</th>
            </tr>
          </thead>
          <tbody>
            {list.map((f) => (
              <tr key={f.id}>
                <td style={S.td}>{f.createdAt?.slice(0, 16) ?? '-'}</td>
                <td style={S.td}>{CATEGORY_LABEL[f.category ?? ''] ?? f.category}</td>
                <td style={{ ...S.td, maxWidth: 360 }}>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{f.content}</div>
                  {f.screenshots && f.screenshots.length > 0 && (
                    <div style={S.shots}>
                      {f.screenshots.map((u, i) => (
                        <a key={i} href={u} target="_blank" rel="noreferrer">
                          <img src={u} style={S.shot} alt={`shot-${i}`} />
                        </a>
                      ))}
                    </div>
                  )}
                </td>
                <td style={S.td}>{f.contact || '-'}</td>
                <td style={S.td}>
                  <code style={{ fontSize: 11 }}>
                    {[f.platform, f.appVersion, f.region]
                      .filter(Boolean)
                      .join(' / ') || '-'}
                  </code>
                </td>
                <td style={S.td}>
                  <Status status={f.status ?? 'open'} />
                  {f.adminNote ? (
                    <div style={S.note} title={f.adminNote}>
                      {f.adminNote}
                    </div>
                  ) : null}
                </td>
                <td style={S.td}>
                  {f.status !== 'in_progress' && f.status !== 'resolved' && (
                    <button
                      style={S.btnSecondary}
                      disabled={busy === f.id}
                      onClick={() => setStatus(f.id, 'in_progress')}
                    >
                      {busy === f.id ? '...' : '处理中'}
                    </button>
                  )}
                  {f.status !== 'resolved' && (
                    <button
                      style={S.btnPrimary}
                      disabled={busy === f.id}
                      onClick={() => setStatus(f.id, 'resolved')}
                    >
                      标记已处理
                    </button>
                  )}
                  {f.status === 'resolved' && (
                    <button
                      style={S.btnSecondary}
                      disabled={busy === f.id}
                      onClick={() => setStatus(f.id, 'open')}
                    >
                      重新打开
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Status({ status }: { status: string }) {
  const map: Record<string, string> = {
    open: '#f5a623',
    in_progress: '#1f78d1',
    resolved: '#1f9d8f',
  };
  const label: Record<string, string> = {
    open: '待处理',
    in_progress: '处理中',
    resolved: '已处理',
  };
  const color = map[status] ?? '#888';
  return (
    <span
      style={{
        padding: '2px 8px',
        borderRadius: 4,
        background: `${color}22`,
        color,
        fontSize: 12,
      }}
    >
      {label[status] ?? status}
    </span>
  );
}

const S: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  refresh: {
    padding: '6px 14px',
    background: 'white',
    border: '1px solid #ddd',
    borderRadius: 6,
    cursor: 'pointer',
  },
  filters: { display: 'flex', gap: 8, marginBottom: 20 },
  filterBtn: {
    padding: '6px 14px',
    background: 'white',
    border: '1px solid #ddd',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 13,
    color: '#444',
  },
  filterBtnActive: {
    background: '#1f9d8f',
    color: 'white',
    borderColor: '#1f9d8f',
  },
  err: { padding: 12, background: '#fee', color: '#d33', borderRadius: 6, marginBottom: 16 },
  loading: { color: '#888', padding: 24 },
  empty: {
    padding: 40,
    textAlign: 'center',
    color: '#888',
    background: 'white',
    borderRadius: 8,
  },
  table: {
    width: '100%',
    background: 'white',
    borderRadius: 8,
    borderCollapse: 'collapse',
    overflow: 'hidden',
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  },
  th: {
    padding: '12px 14px',
    textAlign: 'left',
    fontSize: 12,
    color: '#666',
    background: '#fafafa',
    borderBottom: '1px solid #eee',
  },
  td: {
    padding: '12px 14px',
    fontSize: 13,
    borderBottom: '1px solid #f4f4f4',
    verticalAlign: 'top',
  },
  shots: { display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' },
  shot: {
    width: 48,
    height: 48,
    objectFit: 'cover',
    borderRadius: 4,
    border: '1px solid #eee',
  },
  note: { fontSize: 11, color: '#999', marginTop: 4, maxWidth: 140 },
  btnPrimary: {
    padding: '4px 10px',
    marginRight: 6,
    background: '#1f9d8f',
    color: 'white',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 12,
  },
  btnSecondary: {
    padding: '4px 10px',
    marginRight: 6,
    background: 'white',
    color: '#666',
    border: '1px solid #ccc',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 12,
  },
};
